import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, shortSku, shortWax, fmtDate, getOperator, setOperator, STATUS_LABEL } from './wickTestApi';
import './WickTesting.css';

/**
 * Wick test lab form — the Burn Quality Test sheet (ASTM F-2417) as a phone /
 * tablet page. Public (no login): the QR label on each candidate candle opens
 *
 *   /production/wick-test/<candidate code>      e.g. WT-0012-B
 *
 * Tests 1-3 take readings at hour 1, 2 and 4; tests 4+ take one reading at
 * hour 4. Every reading is checked against the limits in Settings as it is
 * typed. Approve / Fail on the candle records the result; approving also
 * closes the request and marks the other candidates "not selected".
 */
export default function WickTestLab() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState('');
  const [by, setBy] = useState(getOperator());

  if (!code) {
    return (
      <div className="wt-lab">
        <h1>Wick test lab</h1>
        <p className="wt-note">Scan the QR on the candle, or type its code.</p>
        <form className="wt-lab-scan" onSubmit={(e) => { e.preventDefault(); if (entry.trim()) navigate(`/production/wick-test/${entry.trim().toUpperCase()}`); }}>
          <input autoFocus placeholder="WT-0001-A" value={entry} onChange={(e) => setEntry(e.target.value)} />
          <button className="wt-btn" type="submit">Open</button>
        </form>
        <LabQueue />
      </div>
    );
  }

  return <CandidateForm code={code} by={by} setBy={(v) => { setBy(v); setOperator(v); }} />;
}

function LabQueue() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.labQueue().then(setRows).catch(() => setRows([])); }, []);
  if (!rows) return null;
  if (!rows.length) return <div className="wt-note">Nothing is in the lab right now.</div>;
  return (
    <>
      <h2 style={{ fontSize: 15, margin: '16px 0 8px' }}>In the lab</h2>
      {rows.map((r) => (
        <div className="wt-lab-card" key={r.id}>
          <div><strong>{shortSku(r.oil_sku)} · {r.size_label || shortSku(r.vessel_sku)}</strong> <span className="wt-muted">{r.code} · poured {fmtDate(r.poured_at)}</span></div>
          <div className="wt-lab-siblings">
            {(r.candidates || []).map((c) => (
              <Link key={c.id} className="wt-lab-sib" to={`/production/wick-test/${c.code}`}>
                <strong>{c.code.split('-').pop()}</strong> {c.wick_label} <span className={`wt-badge ${c.status}`}>{c.summary?.tests_done || 0} tests</span>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

const HOURS_EARLY = [1, 2, 4];

function nextSlot(readings) {
  const rows = readings || [];
  if (!rows.length) return { test_number: 1, hour: 1 };
  const maxT = Math.max(...rows.map((r) => r.test_number));
  if (maxT <= 3) {
    const hours = rows.filter((r) => r.test_number === maxT).map((r) => r.hour);
    const missing = HOURS_EARLY.find((h) => !hours.includes(h));
    if (missing) return { test_number: maxT, hour: missing };
    return maxT < 3 ? { test_number: maxT + 1, hour: 1 } : { test_number: 4, hour: 4 };
  }
  return { test_number: maxT + 1, hour: 4 };
}

function CandidateForm({ code, by, setBy }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    setError(null);
    api.candidate(code).then((d) => {
      setData(d);
      setForm((f) => f || blankForm(nextSlot(d.candidate?.readings)));
    }).catch((e) => setError(e.response?.data?.error || e.message));
  }, [code]);
  useEffect(() => { setForm(null); load(); }, [load]);

  const limits = data?.settings || {};
  const isMetal = !!data?.request?.is_metal_vessel;
  const maxTemp = isMetal ? limits.max_vessel_temp_metal_f : limits.max_vessel_temp_glass_f;

  const over = useMemo(() => {
    if (!form) return {};
    const n = (v) => (v === '' || v == null ? null : Number(v));
    return {
      vessel_temp_f: n(form.vessel_temp_f) != null && n(form.vessel_temp_f) > Number(maxTemp),
      flame_height_in: n(form.flame_height_in) != null && n(form.flame_height_in) > Number(limits.max_flame_height_in),
      melt_pool_depth_in: n(form.melt_pool_depth_in) != null && n(form.melt_pool_depth_in) > Number(limits.max_melt_pool_depth_in),
      afterglow_s: n(form.afterglow_s) != null && n(form.afterglow_s) > Number(limits.max_afterglow_s),
      aftersmoke_s: n(form.aftersmoke_s) != null && n(form.aftersmoke_s) > Number(limits.max_aftersmoke_s),
    };
  }, [form, maxTemp, limits]);

  if (error) {
    return (
      <div className="wt-lab">
        <div className="wt-error">{error}</div>
        <Link className="wt-btn secondary" to="/production/wick-test">Back</Link>
      </div>
    );
  }
  if (!data || !form) return <div className="wt-lab"><div className="wt-note">Loading…</div></div>;

  const { candidate: c, request: r, siblings } = data;
  const s = c.summary || {};
  const settled = ['approved', 'failed', 'not_selected', 'inconclusive'].includes(c.status);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!by.trim()) { setMsg('Put your initials in first.'); return; }
    setSaving(true);
    setMsg(null);
    try {
      await api.saveReading(code, { ...form, by: by.trim() });
      const d = await api.candidate(code);
      setData(d);
      setForm(blankForm(nextSlot(d.candidate.readings), form.reading_date));
      setMsg('Saved.');
    } catch (e) {
      setMsg(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const editReading = (rd) => setForm({
    test_number: rd.test_number, hour: rd.hour, reading_date: rd.reading_date,
    vessel_temp_f: rd.vessel_temp_f ?? '', flame_height_in: rd.flame_height_in ?? '', melt_pool_depth_in: rd.melt_pool_depth_in ?? '',
    melt_pool_across: rd.melt_pool_across == null ? '' : rd.melt_pool_across ? 'yes' : 'no',
    afterglow_s: rd.afterglow_s ?? '', aftersmoke_s: rd.aftersmoke_s ?? '', notes: rd.notes || '',
  });

  const result = async (kind) => {
    if (!by.trim()) { setMsg('Put your initials in first.'); return; }
    let note = '';
    if (kind === 'approved' && s.recommendation !== 'ready_to_approve') {
      const why = s.recommendation === 'fail' ? `This candle has failing readings:\n${(s.fail_flags || []).join('\n')}` : `Only ${s.tests_done || 0} of ${limits.min_tests_to_approve} tests are done.`;
      if (!window.confirm(`${why}\n\nApprove anyway?`)) return;
      note = window.prompt('Why approve despite that?', '') || '';
      if (!note) return;
    } else {
      note = window.prompt(kind === 'approved' ? 'Approval note (optional)' : 'What went wrong?', '') || '';
      if (kind !== 'approved' && !note && kind === 'failed' && (s.fail_flags || []).length) note = s.fail_flags.join('; ');
    }
    setSaving(true);
    try {
      const d = await api.result(code, { result: kind, note, by: by.trim() });
      setData(d);
      setMsg(kind === 'approved' ? `Approved ${c.wick_label} for ${shortSku(r.oil_sku)} in ${shortSku(r.vessel_sku)}.` : `Marked ${kind}.`);
    } catch (e) {
      setMsg(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const hourChoices = form.test_number <= 3 ? HOURS_EARLY : [4];

  return (
    <div className="wt-lab">
      <div className="wt-toolbar wt-no-print">
        <Link className="wt-link-btn" to="/production/wick-test">← Lab</Link>
        <span className="wt-spacer" />
        <label className="wt-note">Tested by <input className="wt-input" value={by} onChange={(e) => setBy(e.target.value)} placeholder="initials" style={{ width: 80 }} /></label>
      </div>

      <div className="wt-lab-card">
        <h1>{c.code} <span className={`wt-badge ${c.status}`}>{c.status.replace('_', ' ')}</span></h1>
        <div style={{ fontSize: 18, fontWeight: 600 }}>{c.wick_label}</div>
        <dl className="wt-lab-kv" style={{ marginTop: 8 }}>
          <dt>Fragrance</dt><dd>{shortSku(r.oil_sku)}{r.size_label ? ` · ${r.size_label}` : ''}</dd>
          <dt>Vessel</dt><dd>{shortSku(r.vessel_sku)}{isMetal ? ' (metal)' : ''}</dd>
          <dt>Wax</dt><dd>{shortWax(r.wax_sku)}</dd>
          <dt>Wick item</dt><dd>{c.wick_sku || <span className="wt-muted">not a stocked wick</span>}</dd>
          <dt>Why this wick</dt><dd>{c.reason || '—'}</dd>
          <dt>Poured</dt><dd>{r.poured_at ? `${fmtDate(r.poured_at)}${r.poured_by ? ` by ${r.poured_by}` : ''}` : 'not marked poured yet'}</dd>
          <dt>Test</dt><dd>{r.code} · {STATUS_LABEL[r.status]}{r.approved_wick_sku ? ` · approved ${shortSku(r.approved_wick_sku)}` : ''}</dd>
        </dl>
        <div className="wt-lab-siblings">
          {siblings.map((sb) => (
            <Link key={sb.id} className={`wt-lab-sib ${sb.id === c.id ? 'me' : ''}`} to={`/production/wick-test/${sb.code}`}>
              <strong>{sb.code.split('-').pop()}</strong> {sb.wick_label} <span className={`wt-badge ${sb.status}`}>{sb.summary?.tests_done || 0} tests</span>
            </Link>
          ))}
        </div>
      </div>

      <div className="wt-lab-card">
        <h2>Readings</h2>
        <div className="wt-summary">
          <div className="stat"><div className="n">{s.tests_done || 0}<span className="wt-muted" style={{ fontSize: 12 }}> / {limits.min_tests_to_approve}</span></div><div className="l">tests done</div></div>
          <div className="stat"><div className="n" style={{ color: s.max_vessel_temp_f > maxTemp ? '#a23c2b' : undefined }}>{s.max_vessel_temp_f ?? '—'}</div><div className="l">max vessel °F (limit {maxTemp})</div></div>
          <div className="stat"><div className="n">{s.max_flame_height_in ?? '—'}</div><div className="l">max flame in (limit {limits.max_flame_height_in})</div></div>
          <div className="stat"><div className="n">{s.max_melt_pool_depth_in ?? '—'}</div><div className="l">max pool depth (limit {limits.max_melt_pool_depth_in})</div></div>
          <div className="stat"><div className="n">{s.full_pool_at_4h || 0}</div><div className="l">full melt pools at 4 h</div></div>
        </div>
        {s.recommendation === 'fail' && <div className="wt-error">Over limit: {(s.fail_flags || []).join(' · ')}</div>}
        {s.recommendation === 'ready_to_approve' && <div className="wt-ok">All readings in limits across {s.tests_done} tests. Ready to approve.</div>}
        {s.recommendation === 'ready_but_tunneling' && <div className="wt-error">{s.tests_done} tests in limits but the melt pool never reached the edge at hour 4 — likely under-wicked.</div>}
        {c.readings?.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="wt-readings">
              <thead>
                <tr><th>Test</th><th>Hr</th><th>Date</th><th>Vessel °F</th><th>Flame in</th><th>Pool in</th><th>Across</th><th>Glow s</th><th>Smoke s</th><th>Notes</th><th></th></tr>
              </thead>
              <tbody>
                {c.readings.map((rd) => (
                  <tr key={rd.id}>
                    <td>{rd.test_number}</td>
                    <td>{rd.hour}</td>
                    <td>{fmtDate(rd.reading_date)}</td>
                    <td className={rd.vessel_temp_f > maxTemp ? 'over' : ''}>{rd.vessel_temp_f ?? ''}</td>
                    <td className={rd.flame_height_in > limits.max_flame_height_in ? 'over' : ''}>{rd.flame_height_in ?? ''}</td>
                    <td className={rd.melt_pool_depth_in > limits.max_melt_pool_depth_in ? 'over' : ''}>{rd.melt_pool_depth_in ?? ''}</td>
                    <td>{rd.melt_pool_across == null ? '' : rd.melt_pool_across ? 'Y' : 'N'}</td>
                    <td className={rd.afterglow_s > limits.max_afterglow_s ? 'over' : ''}>{rd.afterglow_s ?? ''}</td>
                    <td className={rd.aftersmoke_s > limits.max_aftersmoke_s ? 'over' : ''}>{rd.aftersmoke_s ?? ''}</td>
                    <td style={{ textAlign: 'left', maxWidth: 160 }}>{rd.notes || ''}</td>
                    <td>
                      {!settled && <button className="wt-link-btn" onClick={() => editReading(rd)}>edit</button>}
                      {!settled && <> · <button className="wt-link-btn" onClick={() => { if (window.confirm('Delete this reading?')) api.deleteReading(rd.id).then(load); }}>delete</button></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="wt-note">No readings yet.</div>}
      </div>

      {!settled && (
        <div className="wt-lab-card">
          <h2>Add reading — Test {form.test_number}, hour {form.hour}</h2>
          <div className="wt-form-grid">
            <div className="wt-field">
              <label>Test #</label>
              <input type="number" min={1} max={20} value={form.test_number} onChange={(e) => {
                const t = Math.max(1, Math.min(20, Number(e.target.value) || 1));
                setForm((f) => ({ ...f, test_number: t, hour: t > 3 ? 4 : f.hour }));
              }} />
            </div>
            <div className="wt-field">
              <label>Hour</label>
              <div className="wt-segment">
                {hourChoices.map((h) => <button type="button" key={h} className={form.hour === h ? 'active' : ''} onClick={() => setForm((f) => ({ ...f, hour: h }))}>{h}</button>)}
              </div>
            </div>
            <div className="wt-field">
              <label>Date</label>
              <input type="date" value={form.reading_date} onChange={set('reading_date')} />
            </div>
            <div className="wt-field">
              <label>Highest vessel temp °F</label>
              <input type="number" step="0.1" inputMode="decimal" className={over.vessel_temp_f ? 'over' : ''} value={form.vessel_temp_f} onChange={set('vessel_temp_f')} />
              <div className="limit">limit {maxTemp}°F ({isMetal ? 'metal' : 'glass/ceramic'})</div>
            </div>
            <div className="wt-field">
              <label>Flame height (in)</label>
              <input type="number" step="0.125" inputMode="decimal" className={over.flame_height_in ? 'over' : ''} value={form.flame_height_in} onChange={set('flame_height_in')} />
              <div className="limit">limit {limits.max_flame_height_in}"</div>
            </div>
            <div className="wt-field">
              <label>Melt pool depth (in)</label>
              <input type="number" step="0.125" inputMode="decimal" className={over.melt_pool_depth_in ? 'over' : ''} value={form.melt_pool_depth_in} onChange={set('melt_pool_depth_in')} />
              <div className="limit">limit {limits.max_melt_pool_depth_in}"</div>
            </div>
            <div className="wt-field">
              <label>Melt pool reached the edge?</label>
              <div className="wt-segment">
                {[['yes', 'Yes'], ['no', 'No']].map(([v, l]) => <button type="button" key={v} className={form.melt_pool_across === v ? 'active' : ''} onClick={() => setForm((f) => ({ ...f, melt_pool_across: f.melt_pool_across === v ? '' : v }))}>{l}</button>)}
              </div>
            </div>
            {form.hour === 4 && (
              <>
                <div className="wt-field">
                  <label>Afterglow (seconds)</label>
                  <input type="number" step="1" inputMode="numeric" className={over.afterglow_s ? 'over' : ''} value={form.afterglow_s} onChange={set('afterglow_s')} />
                  <div className="limit">after snuffing · limit {limits.max_afterglow_s}s</div>
                </div>
                <div className="wt-field">
                  <label>Aftersmoke (seconds)</label>
                  <input type="number" step="1" inputMode="numeric" className={over.aftersmoke_s ? 'over' : ''} value={form.aftersmoke_s} onChange={set('aftersmoke_s')} />
                  <div className="limit">limit {limits.max_aftersmoke_s}s</div>
                </div>
              </>
            )}
            <div className="wt-field" style={{ gridColumn: '1 / -1' }}>
              <label>Notes (residue, mushrooming, soot, tunneling…)</label>
              <textarea rows={2} value={form.notes} onChange={set('notes')} />
            </div>
          </div>
          {msg && <div className={/saved|approved|marked/i.test(msg) ? 'wt-ok' : 'wt-error'}>{msg}</div>}
          <div className="wt-modal-actions">
            <button className="wt-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save reading'}</button>
          </div>
        </div>
      )}

      <div className="wt-lab-card">
        <h2>Result</h2>
        {settled ? (
          <div className="wt-note">{c.status.replace('_', ' ')}{c.result_note ? ` — ${c.result_note}` : ''}{c.result_by ? ` (${c.result_by}, ${fmtDate(c.result_at)})` : ''}</div>
        ) : (
          <div className="wt-result-actions">
            <button className="wt-btn approve" disabled={saving} onClick={() => result('approved')}>Approve this wick</button>
            <button className="wt-btn danger" disabled={saving} onClick={() => result('failed')}>Fail</button>
            <button className="wt-btn secondary" disabled={saving} onClick={() => result('inconclusive')}>Inconclusive</button>
          </div>
        )}
        {settled && msg && <div className="wt-ok">{msg}</div>}
      </div>
    </div>
  );
}

function blankForm(slot, date) {
  return {
    test_number: slot.test_number, hour: slot.hour,
    reading_date: date || new Date().toISOString().slice(0, 10),
    vessel_temp_f: '', flame_height_in: '', melt_pool_depth_in: '', melt_pool_across: '',
    afterglow_s: '', aftersmoke_s: '', notes: '',
  };
}
